import React from "react";
import { CircleAlert } from "lucide-react";
import DragNumberInput from "../../ui/DragNumberInput";
import MenuSelect from "../../ui/MenuSelect";

/** Match property panels (e.g. InertialPropertiesPanel); light neutral focus ring, not sky accent */
const INPUT_CLASS =
  "w-full px-2 py-1 text-xs border border-gray-600 rounded bg-gray-800 text-gray-100 placeholder:text-gray-500 focus:outline-none focus:ring-0 focus:border-gray-200";
const ERROR_TEXT_CLASS = "mt-1 text-xs text-rose-300";
const SECTION_LABEL_CLASS = "text-sm font-semibold text-gray-300";
const AXIS_LABEL_CLASS = "text-xs text-gray-400";

function BodySelect({ opts, value, onPick, placeholder = "— body —" }) {
  const hasValue = typeof value === "string" && value.length > 0;
  const hasOption = hasValue ? opts.some((o) => String(o.value) === String(value)) : false;
  const options = [
    { value: "", label: placeholder },
    ...(hasValue && !hasOption
      ? [{ value, label: `Missing body (${String(value).slice(0, 8)})` }]
      : []),
    ...opts.map((o) => ({ value: o.value, label: o.label })),
  ];
  return (
    <MenuSelect
      size="compact"
      className={INPUT_CLASS}
      value={value || ""}
      onChange={onPick}
      options={options}
      contentClassName="max-h-[min(280px,50vh)]"
      triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
      aria-label="Body"
    />
  );
}

function EndEffectorSelect({ opts, value, onPick }) {
  const hasValue = typeof value === "string" && value.length > 0;
  const hasOption = hasValue ? opts.some((o) => String(o.value) === String(value)) : false;
  const options = [
    { value: "", label: "— end-effector —" },
    ...(hasValue && !hasOption ? [{ value, label: `Missing ref (${String(value)})` }] : []),
    ...opts.map((o) => ({ value: o.value, label: o.label })),
  ];
  return (
    <MenuSelect
      size="compact"
      className={INPUT_CLASS}
      value={value || ""}
      onChange={onPick}
      options={options}
      contentClassName="max-h-[min(280px,50vh)]"
      triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
      aria-label="End effector"
    />
  );
}

function Row({ label, children, error }) {
  return (
    <div className="space-y-2">
      <div className={SECTION_LABEL_CLASS}>{label}</div>
      {children}
      {error ? <div className={ERROR_TEXT_CLASS}>{error}</div> : null}
    </div>
  );
}

function RequiredFieldLabel({ children, required = false }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{children}</span>
      {required ? (
        <CircleAlert
          className="h-3.5 w-3.5 shrink-0 text-amber-400"
          aria-label="Required to run this behavior"
          title="Required to run this behavior"
        />
      ) : null}
    </span>
  );
}

function NumericDragRow({
  label,
  fieldKey,
  value,
  onCommit,
  validateNumeric,
  getFieldError,
  min = -Infinity,
  max = Infinity,
  integer = false,
  step = "0.01",
  dragSensitivity,
  /** Smallest increment when scrubbing (defaults from `step`, or 1 when `integer`). */
  dragSnapStep: dragSnapStepProp,
  inputProps: inputPropsExtra = {},
}) {
  const num = Number(value);
  const dragVal = Number.isFinite(num) ? num : 0;
  const applyRaw = (raw) => validateNumeric({ fieldKey, raw, min, max, integer });
  const resolvedDragSnap =
    dragSnapStepProp != null && Number.isFinite(dragSnapStepProp)
      ? dragSnapStepProp
      : integer
        ? 1
        : (() => {
            const s = Number(step);
            return Number.isFinite(s) && s > 0 ? s : 0.01;
          })();
  return (
    <div>
      <DragNumberInput
        label={label}
        value={value}
        dragValue={dragVal}
        onDragValueChange={(v) => {
          const raw = String(integer ? Math.round(v) : v);
          const next = applyRaw(raw);
          if (next != null) onCommit(next);
        }}
        onChange={(e) => {
          const next = applyRaw(e.target.value);
          if (next != null) onCommit(next);
        }}
        wrapperClassName="space-y-2"
        labelClassName={SECTION_LABEL_CLASS}
        inputClassName={INPUT_CLASS}
        inputProps={{
          step,
          ...(Number.isFinite(min) && min !== -Infinity ? { min: String(min) } : {}),
          ...inputPropsExtra,
        }}
        dragSensitivity={dragSensitivity}
        dragSnapStep={resolvedDragSnap}
      />
      {getFieldError(fieldKey) ? (
        <div className={ERROR_TEXT_CLASS}>{getFieldError(fieldKey)}</div>
      ) : null}
    </div>
  );
}

function XYZRow({ keys, node, onChange, fieldPrefix, validateNumeric, getFieldError }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {keys.map((k) => {
        const fieldKey = `${fieldPrefix}.${k}`;
        const num = Number(node[k]);
        const dragVal = Number.isFinite(num) ? num : 0;
        const applyRaw = (raw) => validateNumeric({ fieldKey, raw });
        return (
          <div key={k}>
            <DragNumberInput
              label={k}
              value={node[k]}
              dragValue={dragVal}
              onDragValueChange={(v) => {
                const next = applyRaw(String(v));
                if (next != null) onChange({ ...node, [k]: next });
              }}
              onChange={(e) => {
                const next = applyRaw(e.target.value);
                if (next != null) onChange({ ...node, [k]: next });
              }}
              wrapperClassName="space-y-1"
              labelClassName={AXIS_LABEL_CLASS}
              inputClassName={INPUT_CLASS}
              inputProps={{ step: "0.01" }}
              dragSnapStep={0.01}
            />
            {getFieldError(fieldKey) ? (
              <div className={ERROR_TEXT_CLASS}>{getFieldError(fieldKey)}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export {
  BodySelect,
  EndEffectorSelect,
  ERROR_TEXT_CLASS,
  INPUT_CLASS,
  NumericDragRow,
  RequiredFieldLabel,
  Row,
  XYZRow,
};
